import React, { useState } from 'react';
import { BrregProvider, useBrreg } from '../context/BrregContext';
import ImportJobBuilder from '../components/brreg/ImportJobBuilder';
import StagingBoard from '../components/brreg/StagingBoard';
import SafeIcon from '../common/SafeIcon';
import PermissionGate from '../common/PermissionGate';

const LeadImportContent = () => {
  const { importJobs, loadStagingLeads, refreshJob, isImporting } = useBrreg();
  const [activeTab, setActiveTab] = useState('import');
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [refreshingJobId, setRefreshingJobId] = useState(null);

  // Denne funksjonen håndterer byttet mellom faner
  const handleJobSelect = (jobId) => {
    setSelectedJobId(jobId);
    loadStagingLeads(jobId);
    setActiveTab('staging');
  };

  const handleRefresh = async (event, jobId) => {
    event.stopPropagation();
    setRefreshingJobId(jobId);
    await refreshJob(jobId);
    setRefreshingJobId(null);
  };

  return (
    <div className="p-8 max-w-7xl mx-auto min-h-screen space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-200 pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-[10px] font-black uppercase tracking-widest mb-3 border border-blue-100">
            <SafeIcon icon="🏢" className="w-3 h-3" />
            Add-on
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Lead Import Center</h1>
          <p className="text-slate-500 font-medium text-lg mt-2">Hent kvalitetsleads direkte fra Brønnøysundregistrene.</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex gap-2 bg-slate-100/50 p-1.5 rounded-2xl w-fit border border-slate-200 shadow-sm">
        <button 
          onClick={() => setActiveTab('import')} 
          className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'import' ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`}
        >
          <SafeIcon icon="🔍" className="w-4 h-4" />
          1. Import & Søk
        </button>
        <button 
          onClick={() => setActiveTab('staging')} 
          className={`px-6 py-3 rounded-xl text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${activeTab === 'staging' ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-200/50'}`}
        >
          <SafeIcon icon="🧹" className="w-4 h-4" />
          2. Vask & Distribusjon
        </button>
      </div>

      {/* Content */}
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
        {activeTab === 'import' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {/* Sender med handleJobSelect slik at vi automatisk bytter fane når importen er ferdig */}
              <ImportJobBuilder onJobStarted={handleJobSelect} />
            </div>
            
            <div className="lg:col-span-1">
              <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm h-full flex flex-col">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-100">
                  <h3 className="font-black text-slate-900 text-sm uppercase tracking-widest">Tidligere Jobber</h3>
                  <span className="bg-slate-100 text-slate-500 px-2 py-1 rounded text-[10px] font-bold">{importJobs.length}</span>
                </div>
                
                <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-3">
                  {importJobs.map(job => (
                    <div 
                      key={job.job_id} 
                      onClick={() => handleJobSelect(job.job_id)} 
                      className="p-4 rounded-xl border border-slate-100 hover:border-blue-300 hover:bg-blue-50 hover:shadow-md cursor-pointer transition-all group"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-slate-800 text-sm group-hover:text-blue-700 transition-colors line-clamp-1 pr-2">{job.segment_name}</span>
                        <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-md shrink-0 border ${job.status === 'Fullført' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                          {job.status}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-slate-400 font-bold">
                        <span className="flex items-center gap-1"><SafeIcon icon="📅" /> {new Date(job.created_at).toLocaleDateString()}</span>
                        <span className="flex items-center gap-1"><SafeIcon icon="🏢" /> {job.result_summary?.staged || 0} leads</span>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                        <span className="text-[10px] text-slate-400 font-bold">
                          {job.last_refreshed_at
                            ? `Sist oppdatert ${new Date(job.last_refreshed_at).toLocaleString('no-NO')}`
                            : 'Ikke oppdatert ennå'}
                        </span>
                        <button
                          type="button"
                          onClick={(event) => handleRefresh(event, job.job_id)}
                          disabled={isImporting || refreshingJobId === job.job_id}
                          className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-[10px] font-black uppercase tracking-widest hover:bg-blue-100 disabled:opacity-50"
                        >
                          {refreshingJobId === job.job_id ? 'Oppdaterer...' : 'Oppdater jobb'}
                        </button>
                      </div>
                    </div>
                  ))}
                  
                  {importJobs.length === 0 && (
                    <div className="text-center py-12">
                      <SafeIcon icon="📭" className="w-12 h-12 mx-auto text-slate-200 mb-3" />
                      <p className="text-slate-400 text-sm font-medium">Ingen importjobber kjørt enda.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {activeTab === 'staging' && (
          <StagingBoard jobId={selectedJobId} />
        )}
      </div>
    </div>
  );
};

// Wrap with Provider and PermissionGate
const LeadImportCenter = () => (
  <PermissionGate permission="canImportBrreg">
    <BrregProvider>
      <LeadImportContent />
    </BrregProvider>
  </PermissionGate>
);

export default LeadImportCenter;